import React from "react";
import { MdBolt, MdCheckCircle, MdOpenInNew, MdSchedule } from "react-icons/md";
import shopifyLogo from "../../../../../../assets/images/marketplaces/shopify.png";
import ChannelLogo from "./ChannelLogo";
import { SHOPIFY_APP_URL } from "../salesChannels";
import { useTranslation } from "react-i18next";

// Keys of marketplaces.showcase.perks, .steps and .features (settings namespace).
const PERKS = ["noEntry", "addresses", "rateCarriers"];

const STEPS = ["connect", "arrive", "label"];

const FEATURES = ["import", "products", "status", "rate"];

const ShopifyShowcase = () => {
  const { t } = useTranslation("settings");
  return (
    <>
      <section className="overflow-hidden rounded-xl bg-ui-inverted">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))] gap-[20px] p-[16px] sm:gap-10 sm:p-7">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border-[1px] border-glass-chip-border bg-glass-chip px-2.5 py-1 text-[9.5px] font-semibold uppercase tracking-[.5px] text-white">
              <MdBolt className="h-[13px] w-[13px]" />
              {t("marketplaces.showcase.eyebrow")}
            </span>
            <h2 className="m-[0px] mt-3.5 text-[22px] font-bold leading-[1.25] text-white sm:text-[26px]">
              {t("marketplaces.showcase.titleLine1")}
              <br />
              {t("marketplaces.showcase.titleLine2")}
            </h2>
            <p className="m-[0px] mt-2.5 max-w-xl text-[13px] leading-relaxed text-white opacity-80 [text-wrap:pretty]">
              {t("marketplaces.showcase.intro")}
            </p>
            <div className="mt-[16px] flex flex-wrap gap-1.5">
              {PERKS.map((perk) => (
                <span
                  key={perk}
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border-[1px] border-glass-chip-border bg-glass-chip px-2.5 py-1 text-[10.5px] font-medium text-white"
                >
                  <MdBolt className="h-[13px] w-[13px]" />
                  {t(`marketplaces.showcase.perks.${perk}`)}
                </span>
              ))}
            </div>
          </div>
          <ol className="m-[0px] flex list-none flex-col gap-2 p-0">
            {STEPS.map((step, i) => (
              <li
                key={step}
                className="flex items-start gap-[12px] rounded-lg border-[1px] border-glass-chip-border bg-glass-chip px-3.5 py-[12px]"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[1px] border-glass-chip-border bg-glass-chip-hover text-[11px] font-bold text-white">
                  {i + 1}
                </span>
                <div>
                  <p className="m-[0px] text-xs font-semibold text-white">
                    {t(`marketplaces.showcase.steps.${step}.title`)}
                  </p>
                  <p className="m-[0px] mt-0.5 text-[10.5px] leading-relaxed text-white opacity-75 [text-wrap:pretty]">
                    {t(`marketplaces.showcase.steps.${step}.body`)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="overflow-hidden rounded-xl border-[1px] border-state-success-border bg-ui-surface">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,320px),1fr))] gap-[20px] p-[16px] sm:gap-8 sm:p-6">
          <div className="flex min-w-0 flex-col">
            <div className="flex items-center gap-[12px]">
              <ChannelLogo
                src={shopifyLogo}
                className="h-14 w-14 rounded-xl border-state-success-border"
                imgClassName="max-h-[80%] max-w-[86%]"
              />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="m-[0px] text-base font-semibold text-ui-text">
                    Shopify
                  </h2>
                  <span className="inline-flex items-center gap-1.5 rounded-full border-[1px] border-state-success-border bg-state-success-bg px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-[.4px] text-state-success-text">
                    <span className="h-1.5 w-1.5 rounded-full bg-state-success-dot" />
                    {t("marketplaces.showcase.liveNow")}
                  </span>
                </div>
                <p className="m-[0px] mt-0.5 text-[11px] font-medium text-ui-text-muted">
                  {t("marketplaces.showcase.appName")}
                </p>
              </div>
            </div>
            <p className="m-[0px] mt-[16px] text-xs leading-relaxed text-ui-text-muted">
              {t("marketplaces.showcase.shopifyBody")}
            </p>
            <div className="mt-[20px] flex flex-wrap items-center gap-2">
              <a
                href={SHOPIFY_APP_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-[16px] py-2.5 text-xs font-semibold text-brand-contrast no-underline hover:bg-brand-hover hover:text-brand-contrast hover:no-underline"
              >
                {t("marketplaces.showcase.install")}
                <MdOpenInNew className="h-3.5 w-3.5" />
              </a>
            </div>
            <p className="m-[0px] mt-3.5 text-[10.5px] leading-relaxed text-ui-text-subtle">
              {t("marketplaces.showcase.ourOwn")}
            </p>
          </div>
          {/* At most two columns, so the four features sit 2x2 with the dashed
              card as a full row below instead of leaving an empty cell. */}
          <ul className="m-[0px] grid list-none grid-cols-[repeat(auto-fit,minmax(max(200px,calc(50%_-_5px)),1fr))] content-start gap-2.5 p-0">
            {FEATURES.map((feature) => (
              <li
                key={feature}
                className="rounded-lg border-[1px] border-ui-border bg-ui-surface-sunken p-[12px] dark:bg-ui-bg"
              >
                <div className="flex items-center gap-1.5">
                  <MdCheckCircle className="h-[15px] w-[15px] text-state-success-dot" />
                  <span className="text-[11.5px] font-semibold text-ui-text">
                    {t(`marketplaces.showcase.features.${feature}.title`)}
                  </span>
                </div>
                <p className="m-[0px] mt-1 text-[10.5px] leading-relaxed text-ui-text-muted [text-wrap:pretty]">
                  {t(`marketplaces.showcase.features.${feature}.body`)}
                </p>
              </li>
            ))}
            <li className="col-span-full rounded-lg border-[1px] border-dashed border-state-info-border bg-ui-surface p-[12px]">
              <div className="flex flex-wrap items-center gap-1.5">
                <MdSchedule className="h-[15px] w-[15px] text-brand" />
                <span className="text-[11.5px] font-semibold text-ui-text">
                  {t("marketplaces.showcase.checkoutTitle")}
                </span>
                <span className="whitespace-nowrap rounded-full bg-brand-light px-[7px] py-px text-[9px] font-semibold uppercase tracking-[.4px] text-brand">
                  {t("marketplaces.showcase.inDevelopment")}
                </span>
              </div>
              <p className="m-[0px] mt-1 text-[10.5px] leading-relaxed text-ui-text-muted [text-wrap:pretty]">
                {t("marketplaces.showcase.checkoutBody")}
              </p>
            </li>
          </ul>
        </div>
      </section>
    </>
  );
};

export default ShopifyShowcase;
